import type { FlowProcess } from '@/features/flow/Flow';
import * as Sentry from '@sentry/vue';
import { inject } from 'vue';

import { createRouter, createWebHistory, RouterView } from 'vue-router';

import { beforeInstallmentEnter, beforeNewFlowEnter } from '@/app/router/router-guards';
import { InstallmentStatuses } from '@/entities/Installment';
import { FlowLayout } from '@/layouts/FlowLayout';
import { RenewalLayout } from '@/layouts/RenewalLayout';
import { FLOW_ROOT, MAIN_PRODUCT, NOT_FOUND, PRODUCTS } from '../app.constants';
import {
  installmentOrder,
  installmentSteps,
  newFlowPages,
  newInsuranceOrder,
  NewInsuranceSteps,
  renewalmentOrder,
  /* newInsuranceSteps */
  renewalSteps,
} from '../flow';
import { getLocaleFromUrl } from '../plugins/i18n';

export const flowOrderMap: Record<string, FlowProcess[]> = {
  [PRODUCTS.CAR_INSURANCE]: newInsuranceOrder,
  [PRODUCTS.INSTALLMENT]: installmentOrder,
  [PRODUCTS.RENEWAL]: renewalmentOrder,
};

const isDev = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging';

const { locale } = getLocaleFromUrl();
export const BASE_URL: string
  = {
    en: import.meta.env.VITE_APP_EN_BASE_URL,
    ar: import.meta.env.VITE_APP_AR_BASE_URL,
  }[locale] ?? '/';

const router = createRouter({
  history: createWebHistory(BASE_URL),
  routes: [
    {
      path: '/:locale',
      name: FLOW_ROOT,
      children: [
        {
          path: `${MAIN_PRODUCT}/flow`,
          name: MAIN_PRODUCT,
          component: RouterView,
          children: [
            {
              path: '',
              name: PRODUCTS.CAR_INSURANCE,
              component: FlowLayout,
              beforeEnter: beforeNewFlowEnter,
              meta: {
                flow: PRODUCTS.CAR_INSURANCE,
              },
              children: [
                {
                  path: NewInsuranceSteps.PHONE,
                  name: NewInsuranceSteps.PHONE,
                  component: () => import('@/pages/new/PhonePage.vue'),
                },
                {
                  path: NewInsuranceSteps.PLATE_QID,
                  name: NewInsuranceSteps.PLATE_QID,
                  component: () => import('@/pages/new/PlateQidPage.vue'),
                },
                {
                  path: NewInsuranceSteps.CHASSIS_NUMBER,
                  name: NewInsuranceSteps.CHASSIS_NUMBER,
                  component: () => import('@/pages/new/VinPage.vue'),
                },
                {
                  path: NewInsuranceSteps.CAR_DETAILS,
                  name: NewInsuranceSteps.CAR_DETAILS,
                  component: () => import('@/pages/new/CarDetailsPage.vue'),
                },
                {
                  path: NewInsuranceSteps.MORE_DETAILS,
                  name: NewInsuranceSteps.MORE_DETAILS,
                  component: () => import('@/pages/new/MoreDetailsPage.vue'),
                },
                {
                  path: NewInsuranceSteps.TARIFFS,
                  name: NewInsuranceSteps.TARIFFS,
                  component: () => import('@/pages/new/TariffsPage.vue'),
                },
                {
                  path: NewInsuranceSteps.SELECT_DEDUCTIBLE,
                  name: NewInsuranceSteps.SELECT_DEDUCTIBLE,
                  component: () => import('@/pages/new/SelectDeductiblePage.vue'),
                },
                {
                  path: NewInsuranceSteps.PLATE_NUMBER,
                  name: NewInsuranceSteps.PLATE_NUMBER,
                  component: () => import('@/pages/new/PlateNumberPage.vue'),
                },
                {
                  path: NewInsuranceSteps.INSURED_INFO,
                  name: NewInsuranceSteps.INSURED_INFO,
                  component: () => import('@/pages/new/InsuredInfoPage.vue'),
                },
                {
                  path: NewInsuranceSteps.POLICY_DATE,
                  name: NewInsuranceSteps.POLICY_DATE,
                  component: () => import('@/pages/new/PolicyDatePage.vue'),
                },
                {
                  path: NewInsuranceSteps.QUOTE,
                  name: NewInsuranceSteps.QUOTE,
                  component: () => import('@/pages/new/Quote/ui/QuotePage.vue'),
                },

                {
                  path: newFlowPages.ACTIVE_RENEWABLE_POLICY_FOUND,
                  name: newFlowPages.ACTIVE_RENEWABLE_POLICY_FOUND,
                  component: () => import('@/pages/new/ActiveRenewablePolicyFoundPage.vue'),
                  meta: {
                    isExceptionPage: true,
                    stopFlowProcess: true,
                  },
                },
                {
                  path: newFlowPages.UPCOMING_POLICY_FOUND,
                  name: newFlowPages.UPCOMING_POLICY_FOUND,
                  component: () => import('@/pages/new/UpcomingPolicyFoundPage.vue'),
                  meta: {
                    isExceptionPage: true,
                    stopFlowProcess: true,
                  },
                },
                {
                  path: newFlowPages.INSURANCE_RESULT,
                  name: newFlowPages.INSURANCE_RESULT,
                  component: () => import('@/pages/new/InsuranceResultPage.vue'),
                  meta: {
                    isResultPage: true,
                  },
                },
              ],
            },
            {
              path: PRODUCTS.INSTALLMENT,
              name: PRODUCTS.INSTALLMENT,
              component: FlowLayout,
              beforeEnter: beforeInstallmentEnter,
              meta: {
                flow: PRODUCTS.INSTALLMENT,
              },
              children: [
                {
                  path: 'payment',
                  name: installmentSteps.PAYMENT,
                  component: () => import('@/pages/installment/InstallmentPage.vue'),
                  meta: {
                    stopFlowProcess: true,
                  },
                },
                {
                  path: InstallmentStatuses.NO_INSTALLMENT_FOUND,
                  name: InstallmentStatuses.NO_INSTALLMENT_FOUND,
                  component: () => import('@/pages/installment/NoInstallmentFoundPage.vue'),
                  meta: {},
                },
                {
                  path: InstallmentStatuses.NO_POLICY_FOUND,
                  name: InstallmentStatuses.NO_POLICY_FOUND,
                  component: () => import('@/pages/installment/NoPolicyFoundPage.vue'),
                  meta: {},
                },
                {
                  path: InstallmentStatuses.POLICY_DOWNGRADED,
                  name: InstallmentStatuses.POLICY_DOWNGRADED,
                  component: () => import('@/pages/installment/PolicyWasDowngraded.vue'),
                  meta: {},
                },
                {
                  path: InstallmentStatuses.INSTALLMENT_PAID,
                  name: InstallmentStatuses.INSTALLMENT_PAID,
                  component: () => import('@/pages/installment/InstallmentPaid.vue'),
                  meta: {},
                },
                {
                  path: InstallmentStatuses.PAYMENT_RESULT,
                  name: InstallmentStatuses.PAYMENT_RESULT,
                  component: () => import('@/pages/installment/PaymentResultPage.vue'),
                  meta: {
                    withFeedback: true,
                  },
                },
              ],
            },
            {
              path: PRODUCTS.RENEWAL,
              name: PRODUCTS.RENEWAL,
              component: RenewalLayout,
              meta: {
                flow: PRODUCTS.RENEWAL,
              },
              beforeEnter(to, __, next) {
                if (to.name !== PRODUCTS.RENEWAL) return next();
                const locale = inject('locale');
                // TODO: change first page?
                // @ts-expect-error said no overload, but its works
                next({ name: renewalSteps.RENEW_INSURANCE, params: { locale } });
              },
              children: [
                {
                  path: 'renew-insurance',
                  name: renewalSteps.RENEW_INSURANCE,
                  component: () => import('@/pages/RenewInsurance'),
                },
                {
                  path: 'policy-renewed',
                  name: renewalSteps.POLICY_RENEWED,
                  component: () => import('@/pages/PolicyRenewed'),
                  meta: {
                    withFeedback: true,
                  },
                },
                {
                  path: 'payment-failed',
                  name: renewalSteps.PAYMENT_FAILED,
                  component: () => import('@/pages/PaymentFailed/'),
                },
                {
                  path: 'wrong-link-renewal-page',
                  name: renewalSteps.WRONG_LINK_RENEWAL_PAGE,
                  component: () => import('@/pages/WrongLinkRenewalPage/'),
                },
                {
                  path: 'wrong-link-contact-us',
                  name: renewalSteps.WRONG_LINK_CONTACT_US,
                  component: () => import('@/pages/WrongLinkContactUs/'),
                },
                {
                  path: 'already-renewed',
                  name: renewalSteps.ALREADY_RENEWED,
                  component: () => import('@/pages/AlreadyRenewed/'),
                },
                {
                  path: 'cant-renew',
                  name: renewalSteps.CANT_RENEW,
                  component: () => import('@/pages/CantRenew/'),
                },
                {
                  path: 'payment-link-available',
                  name: renewalSteps.PAYMENT_LINK_AVAILABLE,
                  component: () => import('@/pages/PaymentLinkAvailable/'),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: NOT_FOUND,
      component: () => import('@/pages/NotFound.vue'),
      beforeEnter(_, __, next) {
        if (isDev) return next();

        window.location.href = '/en/404';
      },
    },
  ],
});

router.beforeEach((to, _, next) => {
  const recordWithFlow = to.matched.find(record => record.meta?.flow);
  const flow = recordWithFlow?.meta?.flow as string | undefined;
  if (flow) {
    Sentry.setTag('flow', flow);
  }
  next();
});

export default router;
